<template>
  <div class="w-full h-full py-6">
    <div class="bg-gray-50 rounded-2xl p-4 mb-6">
      <div class="flex justify-between items-center mb-4">
        <div class="text-xl font-semibold">Настройки теста</div>
        <button
            v-if="test.id"
            @click="$emit('deleteTest', test.id)"
            class="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors"
        >
          Удалить тест
        </button>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label class="block text-gray-700 text-sm font-medium mb-2">
            Название теста
          </label>
          <input
              type="text"
              v-model="testData.title"
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#574AEA]"
              placeholder="Введите название теста"
              @change="handleInput"
          />
        </div>

        <div>
          <label class="block text-gray-700 text-sm font-medium mb-2">
            Ограничение времени (минуты)
          </label>
          <input
              type="number"
              v-model.number="testData.timeLimit"
              min="1"
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#574AEA]"
              @change="handleInput"
          />
        </div>

        <div>
          <label class="block text-gray-700 text-sm font-medium mb-2">
            Количество попыток
          </label>
          <input
              type="number"
              v-model.number="testData.attemptsAllowed"
              min="1"
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#574AEA]"
              @change="handleInput"
          />
        </div>

        <div>
          <label class="block text-gray-700 text-sm font-medium mb-2">
            Минимум правильных ответов для прохождения
          </label>
          <input
              type="number"
              v-model.number="testData.requiredCorrectAnswers"
              min="0"
              :max="maxRequiredAnswers"
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#574AEA]"
              @change="handleInput"
          />
          <p v-if="totalQuestions > 0" class="text-xs text-gray-500 mt-1">
            Максимум: {{ maxRequiredAnswers }}
          </p>
        </div>
      </div>

      <div class="mt-5 pt-4 border-t border-gray-200">
        <div class="relative flex items-center gap-3">
          <input
              type="checkbox"
              id="useRandomQuestionsToggle"
              v-model="testData.useRandomQuestions"
              @change="handleInput"
              class="w-4 h-4 text-[#574AEA] border-gray-300 rounded focus:ring-[#574AEA]"
          />
          <label for="useRandomQuestionsToggle" class="font-medium text-gray-800">
            Выбрать случайные вопросы для показа
          </label>

          <div
              class="relative"
              @mouseenter="showRandomTooltip = true"
              @mouseleave="showRandomTooltip = false"
          >
            <svg class="w-4 h-4 text-gray-400 cursor-help" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clip-rule="evenodd"></path>
            </svg>
            <div
                v-if="showRandomTooltip"
                class="absolute bottom-full left-0 mb-2 w-72 p-2 bg-gray-900 text-white text-xs rounded-lg shadow-lg z-10"
            >
              Если включено, пользователю будет показано указанное количество случайно выбранных вопросов из всего теста. Если выключено - пользователь увидит все вопросы.
              <div class="absolute top-full left-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
            </div>
          </div>
        </div>

        <transition name="expand">
          <div v-if="testData.useRandomQuestions" class="mt-4 pl-7">
            <label class="block text-gray-700 text-sm font-medium mb-2">
              Количество вопросов для показа (из {{ totalQuestions }})
            </label>
            <input
                type="number"
                v-model.number="testData.numberOfQuestionsToDisplay"
                :max="totalQuestions"
                min="1"
                @change="handleInput"
                class="w-full md:w-1/2 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#574AEA]"
                :disabled="totalQuestions === 0"
            />
            <p v-if="totalQuestions === 0" class="text-xs text-red-500 mt-1">
              Сначала добавьте вопросы в тест.
            </p>
          </div>
        </transition>
      </div>
    </div>

    <div class="bg-gray-50 rounded-2xl p-4">
      <div class="flex justify-between items-center mb-4">
        <div class="text-xl font-semibold">Вопросы теста</div>
        <button
            @click="addQuestion"
            class="px-4 py-2 bg-[#574AEA] text-white rounded-md hover:bg-[#473add] transition-colors"
        >
          Добавить вопрос
        </button>
      </div>

      <div v-if="!testData.questions || testData.questions.length === 0" class="text-center py-8 text-gray-500">
        Вопросов пока нет. Добавьте первый вопрос.
      </div>

      <div v-else class="space-y-4">
        <div
            v-for="(question, index) in testData.questions"
            :key="question.id || index"
            class="rounded-2xl bg-white shadow-sm"
        >
          <div class="p-3 flex items-center justify-between cursor-pointer hover:bg-gray-50 transition-colors"
               @click="toggleQuestion(index)">
            <div class="flex items-center gap-4 flex-1">
              <div class="w-8 h-8 bg-[#574AEA] text-white rounded-lg flex items-center justify-center font-semibold">
                {{ index + 1 }}
              </div>
              <div class="flex-1 min-w-0">
                <div class="font-medium text-gray-900 truncate">Вопрос {{ index + 1 }}</div>
                <div v-if="!expandedQuestions.includes(index)" class="text-sm text-gray-600 mt-1 line-clamp-1" v-html="getQuestionPreview(question.question)"></div>
              </div>
            </div>
            <div class="flex items-center gap-2 ml-2">
              <div v-if="question.isCritical" class="px-2 py-1 bg-red-100 text-red-800 text-xs rounded-full flex-shrink-0">
                Критичный
              </div>
              <button
                  @click.stop="confirmDeleteQuestion(index)"
                  class="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                  title="Удалить вопрос"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                </svg>
              </button>
              <button class="p-2 text-gray-400 hover:text-gray-600 transition-colors">
                <svg class="w-5 h-5 transform transition-transform duration-200"
                     :class="{ 'rotate-180': expandedQuestions.includes(index) }"
                     fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
                </svg>
              </button>
            </div>
          </div>

          <transition name="expand">
            <div v-if="expandedQuestions.includes(index)" class="px-4 pb-4 border-t border-gray-100">
              <div class="mb-4 mt-4 max-w-full">
                <label class="block text-gray-700 text-sm font-medium mb-2">
                  Текст вопроса
                </label>
                <div class="max-w-full overflow-hidden">
                  <UQuillEditor
                      :key="`question-${question.id || index}`"
                      v-model="question.question"
                      @blur="handleInput()"
                      @update:content="handleInput()"
                      height="200px"
                  />
                </div>
              </div>

              <div class="mb-4">
                <div class="flex justify-between items-center mb-2">
                  <label class="block text-gray-700 text-sm font-medium">
                    Варианты ответов
                  </label>
                  <button
                      @click="addOption(index)"
                      class="text-[#574AEA] hover:text-[#473add] text-sm"
                  >
                    + Добавить вариант
                  </button>
                </div>

                <div class="space-y-2">
                  <div
                      v-for="(option, optionIndex) in question.options"
                      :key="optionIndex"
                      class="flex items-center gap-3"
                  >
                    <input
                        type="checkbox"
                        :checked="question.correctAnswers.includes(option)"
                        @change="toggleCorrectAnswer(index, option)"
                        class="w-4 h-4 text-[#574AEA] border-gray-300 rounded focus:ring-[#574AEA]"
                    />
                    <input
                        type="text"
                        v-model="question.options[optionIndex]"
                        class="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#574AEA]"
                        placeholder="Введите вариант ответа"
                        @change="handleInput()"
                    />
                    <button
                        v-if="question.options.length > 1"
                        @click="removeOption(index, optionIndex)"
                        class="text-red-500 hover:text-red-700"
                    >
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                      </svg>
                    </button>
                  </div>
                </div>
              </div>

              <div class="space-y-3">
                <div class="flex items-center gap-2 relative">
                  <input
                      type="checkbox"
                      v-model="question.isSingleAnswer"
                      @change="handleSingleAnswerChange(index)"
                      class="w-4 h-4 text-[#574AEA] border-gray-300 rounded focus:ring-[#574AEA]"
                  />
                  <label class="text-sm text-gray-700">Один правильный ответ</label>
                  <div
                      class="relative"
                      @mouseenter="showSingleAnswerTooltip = index"
                      @mouseleave="showSingleAnswerTooltip = -1"
                  >
                    <svg class="w-4 h-4 text-gray-400 cursor-help" fill="currentColor" viewBox="0 0 20 20">
                      <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clip-rule="evenodd"></path>
                    </svg>

                    <div
                        v-if="showSingleAnswerTooltip === index"
                        class="absolute bottom-full left-0 mb-2 w-64 p-2 bg-gray-900 text-white text-xs rounded-lg shadow-lg z-10"
                    >
                      Если отмечено, пользователь сможет выбрать только один вариант ответа. Если не отмечено - несколько вариантов
                      <div class="absolute top-full left-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                    </div>
                  </div>
                </div>

                <div class="flex items-center gap-2 relative">
                  <input
                      type="checkbox"
                      v-model="question.isCritical"
                      @change="handleInput()"
                      class="w-4 h-4 text-[#574AEA] border-gray-300 rounded focus:ring-[#574AEA]"
                  />
                  <label class="text-sm text-gray-700">Критичный вопрос</label>
                  <div
                      class="relative"
                      @mouseenter="showTooltip = index"
                      @mouseleave="showTooltip = -1"
                  >
                    <svg class="w-4 h-4 text-gray-400 cursor-help" fill="currentColor" viewBox="0 0 20 20">
                      <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clip-rule="evenodd"></path>
                    </svg>

                    <div
                        v-if="showTooltip === index"
                        class="absolute bottom-full left-0 mb-2 w-64 p-2 bg-gray-900 text-white text-xs rounded-lg shadow-lg z-10"
                    >
                      Если вопрос отмечен как критичный, неправильный ответ на него автоматически завершит тест с неудачным результатом
                      <div class="absolute top-full left-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </transition>
        </div>
      </div>
    </div>

    <div v-if="showDeleteConfirm" class="fixed inset-0 bg-white/80 backdrop-blur-sm z-50 flex items-center justify-center">
      <div class="bg-white rounded-2xl p-8 max-w-md mx-4 shadow-2xl border border-gray-200">
        <div class="text-center">
          <div class="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg class="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
            </svg>
          </div>
          <div class="text-lg font-semibold text-gray-900 mb-2">Удалить вопрос?</div>
          <p class="text-gray-600 mb-6">Вы точно хотите удалить этот вопрос? Это действие нельзя отменить.</p>

          <div class="flex gap-3">
            <button
                @click="cancelDeleteQuestion"
                class="flex-1 py-2 px-4 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            >
              Отмена
            </button>
            <button
                @click="deleteQuestion"
                class="flex-1 py-2 px-4 bg-red-600 text-white rounded-lg hover:bg-red-700"
            >
              Удалить
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { defineProps, defineEmits, ref, watch, computed } from 'vue'
import UQuillEditor from '@/components/ui/UQuillEditor.vue'
import Quill from 'quill'
import apiService from '@/services/api'
import { useToast } from "vue-toastification"

const props = defineProps({
  test: {
    type: Object,
    required: true,
    default: () => ({
      title: '',
      timeLimit: 30,
      attemptsAllowed: 3,
      requiredCorrectAnswers: 0,
      questions: [],
      useRandomQuestions: false,
      numberOfQuestionsToDisplay: null
    })
  },
  courseId: {
    type: String,
    required: true
  }
})

const emit = defineEmits(['update:test', 'deleteTest'])

const toast = useToast()

const showTooltip = ref(-1)
const showSingleAnswerTooltip = ref(-1)
const showRandomTooltip = ref(false)

const testData = ref(JSON.parse(JSON.stringify(props.test)))

// Инициализация поля isSingleAnswer для существующих вопросов
if (testData.value.questions) {
  testData.value.questions.forEach(question => {
    if (!Object.prototype.hasOwnProperty.call(question, 'isSingleAnswer')) {
      // Для старых вопросов определяем по количеству правильных ответов
      question.isSingleAnswer = question.correctAnswers && question.correctAnswers.length === 1
    }
  })
}

const totalQuestions = computed(() => testData.value.questions?.length || 0)
const maxRequiredAnswers = computed(() => {
  if (testData.value.useRandomQuestions && testData.value.numberOfQuestionsToDisplay > 0) {
    return testData.value.numberOfQuestionsToDisplay
  }
  return totalQuestions.value
})

// --- НОВАЯ НАДЕЖНАЯ ЛОГИКА СИНХРОНИЗАЦИИ ---

// Функция, которая приводит данные в порядок
const normalizeAndValidate = (data) => {
  const normalized = JSON.parse(JSON.stringify(data))
  const total = normalized.questions?.length || 0

  // 1. Синхронизируем галочку с данными
  if (typeof normalized.numberOfQuestionsToDisplay === 'number' && normalized.numberOfQuestionsToDisplay > 0) {
    normalized.useRandomQuestions = true
  }

  // 2. Валидируем количество случайных вопросов
  if (normalized.useRandomQuestions) {
    let numToDisplay = parseInt(normalized.numberOfQuestionsToDisplay, 10) || 0
    if (total === 0) {
      numToDisplay = 0
    } else {
      if (numToDisplay > total || numToDisplay === 0) {
        numToDisplay = total
      }
      if (numToDisplay < 1) {
        numToDisplay = 1
      }
    }
    normalized.numberOfQuestionsToDisplay = numToDisplay
  }

  // 3. Валидируем минимальное количество правильных ответов
  let required = parseInt(normalized.requiredCorrectAnswers, 10) || 0
  const maxReq = normalized.useRandomQuestions ? normalized.numberOfQuestionsToDisplay : total
  if (required > maxReq) {
    required = maxReq
  }
  if (required < 0) {
    required = 0
  }
  normalized.requiredCorrectAnswers = required

  return normalized
}


// Следим за внешними изменениями (когда открывается новый тест)
watch(() => props.test, (newTestData) => {
  // Приводим новые данные в порядок
  const correctedData = normalizeAndValidate(newTestData)

  // Обновляем наше внутреннее состояние
  testData.value = correctedData

  // Если наши исправления отличаются от того, что пришло,
  // немедленно сообщаем родителю, чтобы он тоже обновился.
  if (JSON.stringify(correctedData) !== JSON.stringify(newTestData)) {
    emit('update:test', correctedData)
  }
}, { deep: true, immediate: true }) // immediate: true - чтобы сработало при первой загрузке


// Обработчик для действий пользователя (ввод, клик)
const handleInput = () => {
  // Приводим текущие данные в порядок после ввода пользователя
  const correctedData = normalizeAndValidate(testData.value)
  testData.value = correctedData

  // Сообщаем родителю об изменениях
  emit('update:test', correctedData)
}

// --- КОНЕЦ НОВОЙ ЛОГИКИ ---


const addQuestion = () => {
  if (!testData.value.questions) {
    testData.value.questions = []
  }
  const newIndex = testData.value.questions.length

  testData.value.questions.push({
    question: '',
    options: [''],
    correctAnswers: [],
    isCritical: false,
    isSingleAnswer: false
  })

  expandedQuestions.value.push(newIndex)
  handleInput()
}

const addOption = (questionIndex) => {
  testData.value.questions[questionIndex].options.push('')
  handleInput()
}

const removeOption = (questionIndex, optionIndex) => {
  const question = testData.value.questions[questionIndex]
  const removedOption = question.options[optionIndex]

  question.options.splice(optionIndex, 1)

  const correctIndex = question.correctAnswers.indexOf(removedOption)
  if (correctIndex > -1) {
    question.correctAnswers.splice(correctIndex, 1)
  }

  handleInput()
}

const toggleCorrectAnswer = (questionIndex, option) => {
  const question = testData.value.questions[questionIndex]
  const index = question.correctAnswers.indexOf(option)

  if (index > -1) {
    // Убираем ответ из правильных
    question.correctAnswers.splice(index, 1)
  } else {
    // Добавляем ответ в правильные
    if (question.isSingleAnswer) {
      // Если разрешен только один правильный ответ, заменяем все предыдущие
      question.correctAnswers = [option]
    } else {
      // Если разрешено несколько правильных ответов, добавляем к существующим
      question.correctAnswers.push(option)
    }
  }

  handleInput()
}

const handleSingleAnswerChange = (questionIndex) => {
  const question = testData.value.questions[questionIndex]
  
  // Если включен режим "один правильный ответ" и уже выбрано несколько ответов
  if (question.isSingleAnswer && question.correctAnswers.length > 1) {
    // Оставляем только первый правильный ответ
    question.correctAnswers = question.correctAnswers.slice(0, 1)
  }
  
  handleInput()
}

const expandedQuestions = ref([])

const toggleQuestion = (index) => {
  const qIndex = expandedQuestions.value.indexOf(index)
  if (qIndex > -1) {
    expandedQuestions.value.splice(qIndex, 1)
  } else {
    expandedQuestions.value.push(index)
  }
}

const showDeleteConfirm = ref(false)
const deleteQuestionIndex = ref(null)

const confirmDeleteQuestion = (index) => {
  deleteQuestionIndex.value = index
  showDeleteConfirm.value = true
}

const cancelDeleteQuestion = () => {
  deleteQuestionIndex.value = null
  showDeleteConfirm.value = false
}

const deleteQuestion = async () => {
  if (deleteQuestionIndex.value !== null) {
    const question = testData.value.questions[deleteQuestionIndex.value]

    try {
      if (question.id && props.test.id) {
        await apiService.deleteQuestion(props.courseId, props.test.id, question.id)
        toast.success('Вопрос успешно удален')
      }

      testData.value.questions.splice(deleteQuestionIndex.value, 1)
      handleInput()

    } catch (error) {
      console.error('Ошибка при удалении вопроса:', error)
      toast.error('Ошибка при удалении вопроса')
    } finally {
      deleteQuestionIndex.value = null
      showDeleteConfirm.value = false
    }
  }
}

const getQuestionPreview = (question) => {
  if (!question) return ''
  const stripped = question.replace(/<[^>]+>/g, '') // Убираем HTML теги
  return stripped.length > 150 ? stripped.slice(0, 150) + '...' : stripped
}

// Убедимся, что Quill инициализируется после DOM
Quill.register('ui/icons', {
  'table': '<svg viewBox="0 0 18 18"><rect class="ql-stroke" height="12" width="12" x="3" y="3"></rect><rect class="ql-fill" height="2" width="3" x="5" y="5"></rect><rect class="ql-fill" height="2" width="3" x="8" y="5"></rect><rect class="ql-fill" height="2" width="3" x="11" y="5"></rect><rect class="ql-fill" height="2" width="3" x="5" y="8"></rect><rect class="ql-fill" height="2" width="3" x="8" y="8"></rect><rect class="ql-fill" height="2" width="3" x="11" y="8"></rect><rect class="ql-fill" height="2" width="3" x="5" y="11"></rect><rect class="ql-fill" height="2" width="3" x="8" y="11"></rect><rect class="ql-fill" height="2" width="3" x="11" y="11"></rect></svg>'
});

</script>

<style scoped>
/* Анимация для сворачивания/разворачивания */
.expand-enter-active,
.expand-leave-active {
  transition: all 0.3s ease-in-out;
  overflow: hidden;
  max-height: 1000px; /* Увеличил для Quill */
}

.expand-enter-from,
.expand-leave-to {
  opacity: 0;
  max-height: 0;
  margin-top: 0;
  padding-top: 0;
  padding-bottom: 0;
}

.line-clamp-1 {
  display: -webkit-box;
  -webkit-line-clamp: 1;
  line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
